import { ReactNode } from "react";
import { FaCircleCheck, FaCirclePlus } from "react-icons/fa6";
import { cn } from "@/lib/utils";

export interface Option {
  id: string;
  name: string;
}

// Props for when multiSelect is false or undefined
type SingleSelectProps<T extends Option> = {
  multiSelect?: false;
  selected: T | null;
  handleSelected: (selected: T | null) => void;
};

// Props for when multiSelect is true
type MultiSelectProps<T extends Option> = {
  multiSelect: true;
  selected: T[];
  handleSelected: (selected: T[]) => void;
};

type Props<T extends Option> = (SingleSelectProps<T> | MultiSelectProps<T>) & {
  options: T[];
  classNames?: {
    list?: string;
    item?: string;
  };
  renderOption?: (option: T, isSelected: boolean) => ReactNode;
  renderLabel?: (option: T) => ReactNode;
};

const SelectionFilter = <T extends Option>(props: Props<T>) => {
  const { options, classNames = {}, renderOption, renderLabel } = props;

  const handleOptionClick = (option: T) => {
    if (props.multiSelect) {
      // is this filter is multiselect
      const isCurrentlySelected = props.selected.some(
        (s) => s.id === option.id
      );
      // remove from selected if already selected, else add to selected
      const newSelected = isCurrentlySelected
        ? props.selected.filter((s) => s.id !== option.id)
        : [...props.selected, option];
      props.handleSelected(newSelected);
    } else {
      // this filter is single select
      const isAlreadySelected = props.selected?.id === option.id;
      const newSelected = isAlreadySelected ? null : option;
      props.handleSelected(newSelected);
    }
  };

  // const defaultOption = (option: T, isSelected: boolean) => (
  //   <>
  //     <span className="text-sm flex-1 pe-1 text-muted-foreground">
  //       {option.name}
  //     </span>
  //     {isSelected ? (
  //       <FaCircleCheck className="text-green-600 hover:text-green-500 transition-colors ease-linear" />
  //     ) : (
  //       <FaCirclePlus
  //         size="1rem"
  //         className="text-blue-600 hover:text-blue-500 transition-colors ease-linear"
  //       />
  //     )}
  //   </>
  // );

  const defaultOption = (option: T, isSelected: boolean) => (
    <>
      <div className="text-sm flex-1 pe-1 text-muted-foreground break-words">
        {renderLabel ? renderLabel(option) : option.name}
      </div>

      {/* Icon Area */}
      <div className="shrink-0">
        {isSelected ? (
          <FaCircleCheck className="text-green-600 hover:text-green-500 transition-colors ease-linear" />
        ) : (
          <FaCirclePlus
            size="1rem"
            className="text-blue-600 hover:text-blue-500 transition-colors ease-linear"
          />
        )}
      </div>
    </>
  );

  return (
    <ul
      className={cn(
        "overflow-y-auto max-h-52 lg:max-h-64 grid grid-cols-2 md:grid-cols-3 p-2 w-full gap-4",
        classNames.list
      )}
    >
      {options.map((option) => {
        const isSelected = props.multiSelect
          ? props.selected.some((o) => o.id === option.id)
          : props.selected?.id === option.id;

        return (
          <li
            key={option.id}
            className={cn(
              "flex items-center justify-between cursor-pointer w-full min-h-12 px-2 py-1 rounded-sm bg-white border border-gray-400",
              classNames.item
            )}
            onClick={() => {
              handleOptionClick(option);
            }}
          >
            {renderOption
              ? renderOption(option, isSelected)
              : defaultOption(option, isSelected)}
          </li>
        );
      })}
    </ul>
  );
};

export default SelectionFilter;
